import { useParams } from 'react-router';
import {
  BorderedSection,
  Button,
  FilterHeader,
  Footer,
  Loading,
  SaveInstructions,
  TitleBar,
} from '../../../components';
import ListManager from '../../../components/common/ListManager';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import {
  ASSIGN_LOCATIONS_TO_MIITGATE,
  GET_ALL_LOCATIONS,
  GET_LOCATIONS_TO_MITIGATE,
  UNASSIGN_LOCATIONS_TO_MIITGATE,
} from '../queries/locationSelection';
import { useUserContext } from '../../../context/PermissionsContext';
import TitleUpdater from '../../../components/common/TitleUpdater';

type Location = {
  __typename: 'Location';
  uuid: string;
  locationName: string;
  locationType?: string;
};

const MitigationLocationSelection = () => {
  const params = useParams();
  let [searchParams] = useSearchParams();
  const divisionName = searchParams.get('division')!;
  const divisionId = params.divisionId!;
  const locationId = searchParams.get('locationId')!;
  const locationName = searchParams.get('locationName')!;
  const level = searchParams.get('level')!;

  const [addedLocations, setAddedLocations] = useState<Location[]>([]);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);

  const { canEdit } = useUserContext();

  const orderedAvailableLocations = [...filteredLocations].sort((a, b) => {
    return a.locationName.localeCompare(b.locationName);
  });

  const orderedAddedLocations = [...addedLocations].sort((a, b) => {
    return a.locationName.localeCompare(b.locationName);
  });

  const [search, setSearch] = useState('');

  const { data: allLocations, loading: loadingLocations } = useQuery<{
    findAllLocations: Location[];
  }>(GET_ALL_LOCATIONS);

  const { data: locationsToMitigate, loading: loadingMitigatedLocations } =
    useQuery<{ getLocationsByDivisionAndParent: Location[] }>(
      GET_LOCATIONS_TO_MITIGATE,
      {
        variables: {
          data: {
            divisionId: divisionId,
            parentId: locationId,
          },
        },
        skip: !divisionId || !locationId,
      },
    );

  const [AssignLocationsToMitigate, { loading: assignLocationsLoading }] =
    useMutation(ASSIGN_LOCATIONS_TO_MIITGATE, {
      refetchQueries: ['GetLocationsToMitigate'],
    });

  const [UnassignLocationsToMitigate, { loading: unassignLocationsLoading }] =
    useMutation(UNASSIGN_LOCATIONS_TO_MIITGATE, {
      refetchQueries: ['GetLocationsToMitigate'],
    });

  const locationsToMitigateData =
    locationsToMitigate?.getLocationsByDivisionAndParent || [];

  const loadingStates = loadingLocations || loadingMitigatedLocations;

  const flattenLocationsToMitigate = useMemo(() => {
    if (!locationsToMitigateData) return [];
    return locationsToMitigateData.map((loc: any) => loc.locationId);
  }, [locationsToMitigateData]);

  useEffect(() => {
    if (!allLocations) return;

    const locationsToShow = allLocations.findAllLocations.filter(
      (location: any) =>
        location.uuid !== locationId &&
        !flattenLocationsToMitigate.includes(location.uuid),
    );

    setAvailableLocations(locationsToShow);
    setFilteredLocations(locationsToShow);
  }, [allLocations, flattenLocationsToMitigate, locationId]);

  useEffect(() => {
    if (!allLocations) return;

    const mitigatedLocations = allLocations.findAllLocations.filter(
      (location: any) => flattenLocationsToMitigate.includes(location.uuid),
    );

    setAddedLocations(mitigatedLocations);
  }, [allLocations, flattenLocationsToMitigate]);

  const onAddItem = (item: Location) => {
    setAddedLocations((prev) => [...prev, item]);
    setAvailableLocations((prev) =>
      prev.filter((loc) => loc.uuid !== item.uuid),
    );
    setFilteredLocations((prev) =>
      prev.filter((loc) => loc.uuid !== item.uuid),
    );
  };

  const onRemoveItem = (item: Location) => {
    setAvailableLocations((prev) => [...prev, item]);
    setFilteredLocations((prev) => [...prev, item]);
    setAddedLocations((prev) => prev.filter((loc) => loc.uuid !== item.uuid));
  };

  const onSaveChanges = async () => {
    if (!canEdit) {
      message.info('You do not have permission to edit this data');
      return;
    }

    const locationsToMitigate = addedLocations.map((loc) => loc.uuid);

    try {
      await AssignLocationsToMitigate({
        variables: {
          data: {
            divisionId: divisionId,
            parentId: locationId,
            locationIds: locationsToMitigate,
          },
        },
      });
    } catch (error) {
      message.error('Failed to add locations');
    }

    const locationsToRemove = availableLocations.map((loc) => loc.uuid);

    try {
      await UnassignLocationsToMitigate({
        variables: {
          data: {
            divisionId: divisionId,
            parentId: locationId,
            locationIds: locationsToRemove,
          },
        },
      });
    } catch (error) {
      message.error('Failed to remove locations');
    }

    message.success('Locations updated successfully');
  };

  const filterAvailableLocations = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);
    const filtered = availableLocations.filter((location) =>
      location.locationName.toLowerCase().includes(value),
    );
    setFilteredLocations(filtered);
  };

  return (
    <>
      <TitleUpdater title={`Internal Mitigation Selection`} />
      <TitleBar
        title="Internal Mitigation Options"
        subtitle={`${divisionName} // ${locationName}`}
        brief={`From the list of internal locations below, select which can support the production of products at the ${locationName} location. If a location is missing, go to the ${'Data Inputs'} section to add the location.`}
      />
      <BorderedSection
        title=""
        noPadding
        headingClassName="h-20"
        contentClassName="py-4"
        extendOptions={
          <FilterHeader
            searchTerm={search}
            onSearch={filterAvailableLocations}
          />
        }
      >
        {!loadingStates ? (
          <ListManager
            addedItems={orderedAddedLocations}
            availableItems={orderedAvailableLocations}
            onRemoveItem={onRemoveItem}
            onAddItem={onAddItem}
            addedTitle="INTERNAL LOCATIONS ADDED"
            availableTitle="INTERNAL LOCATIONS AVAILABLE"
            getItemLabel={(item: any) => item.locationName}
            infoContent={<SaveInstructions isLocation={!!locationName} />}
          />
        ) : (
          <Loading />
        )}
      </BorderedSection>
      <div className="flex items-center justify-center mt-4">
        <Button
          onClick={onSaveChanges}
          type="primary"
          className="text-sm mt-4 min-w-[180px]"
          disabled={assignLocationsLoading || unassignLocationsLoading}
        >
          Save
        </Button>
      </div>
      <Footer
        backPath={`/${divisionId}/mitigation?division=${divisionName}`}
        nextPath={`/${divisionId}/internal-mitigation?division=${divisionName}&location=${locationId}&locationName=${locationName}&level=${level}`}
      />
    </>
  );
};
export default MitigationLocationSelection;
